import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { useSurveyTabAutoClose } from '../../hooks/useSurveyTabClose';

export default function SurveyTerminatePage() {
  const [countdown, setCountdown] = useState(8);
  const { showCloseButton } = useSurveyTabAutoClose({ type: 'security-terminate' });

  // Self-sufficient forward path, mirroring SurveyScreenoutPage. On DESKTOP the
  // survey tab is script-closed ~2s after mount by useSurveyTabAutoClose, so this
  // countdown only ever completes on a same-tab (mobile / in-app) landing — which
  // is exactly the case that previously dead-ended on this page with no way out.
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = '/surveys';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center space-y-6"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        >
          <ShieldOff className="w-20 h-20 text-[#9CA3AF] mx-auto" strokeWidth={1.5} />
        </motion.div>

        <h1 className="text-2xl font-bold font-display text-[#111827]">
          Survey Ended
        </h1>

        <p className="text-sm text-[#6B7280]">
          This survey ended early. Contact support if you think this was an error.
        </p>

        <p className="text-sm text-[#9CA3AF]">
          No points awarded
        </p>

        <div className="flex flex-col gap-3 pt-2">
          <Link
            to="/surveys"
            className="py-3 rounded-full bg-[#5B4FE8] text-white text-sm font-medium hover:bg-[#4338CA] transition-colors text-center"
          >
            Browse Surveys
          </Link>
          <Link
            to="/dashboard"
            className="py-3 rounded-full border-2 border-[#E5E7EB] text-[#111827] text-sm font-medium hover:bg-gray-50 transition-colors text-center"
          >
            Go to Dashboard
          </Link>
          <a
            href="mailto:support@swipeopinion.com"
            className="text-sm text-[#5B4FE8] hover:text-[#4338CA] font-medium"
          >
            Contact Support
          </a>
          {showCloseButton ? (
            <button
              onClick={() => window.close()}
              className="text-sm text-[#5B4FE8] font-medium hover:underline"
            >
              Close this tab
            </button>
          ) : (
            <p className="text-xs text-[#9CA3AF]">
              Redirecting to surveys in {countdown}s...
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
